import { useCallback, useEffect, useState } from 'react';
import { COL_DEFS } from '../constants/columns';
import { GetCellValue as getCellValue } from '../pipeline/derive/CellValue';

const STORAGE_KEY_PREFIX = 'op:colW:';
// Min/max sane bounds. 30px = altura da row (mantém coluna do carrinho
// quadrada quando vazia); 600px é mais que qualquer label/conteúdo razoável
// e impede um drag acidental de quebrar layout. Colunas normais nunca caem
// abaixo de 50 porque labels exigem ~38px mínimos via HEADER_OVERHEAD.
const MIN_W = 30;
const MAX_W = 600;
// Espaço fixo reservado pro indicador de sort + paddings + safety (estimado).
const HEADER_OVERHEAD_PX = 38;
// Padding lateral do TD da linha de dados (8px cada lado).
const CELL_OVERHEAD_PX = 24;
// Quando uma coluna é ordenada e o label + paddings já não deixa folga pro ícone,
// crescemos o suficiente pra encaixar: 11px do ícone + 5px de gap = 16px (+ safety).
const SORT_ICON_SLOT_PX = 18;
// Padding horizontal total da célula do header (10px cada lado).
const HEADER_PADDING_LR_PX = 20;

/**
 * Fonte usada na medição via canvas pelo autofit/sort-aware. Exportada também
 * pra DataTable usar quando renderizar (precisam ficar em sync — se mudar aqui,
 * mudar lá tb).
 */
export const HEADER_FONT = '600 11px Inter, -apple-system, system-ui, sans-serif';
export const CELL_FONT = '12px "JetBrains Mono", ui-monospace, SFMono-Regular, monospace';

/**
 * Hook: larguras de colunas por preset, com override em localStorage.
 *
 * Cada preset tem seu próprio mapa (`op:colW:${preset.id}`). Voltar pro padrão
 * é deletar a chave (function `resetColumn`/`resetAll`). `setWidth` persiste
 * imediatamente — sem debounce — assim recarregar a página mantém a alteração.
 */
export function useColumnWidths(presetId) {
    const storageKey = `${STORAGE_KEY_PREFIX}${presetId || 'default'}`;
    const [widthOverrides, setWidthOverrides] = useState(() => load(storageKey));

    // Quando o preset muda, troca o storage e recarrega o mapa.
    useEffect(() => {
        setWidthOverrides(load(storageKey));
    }, [storageKey]);

    // Persist usa functional update pra evitar stale closure quando chamado em
    // batch (ex: autofit-all dispara setWidth pra cada coluna em loop síncrono;
    // sem isso, cada chamada partia do mesmo `widthOverrides` em closure e só
    // a ÚLTIMA persistia).
    const persistFn = useCallback(
        (mutator) => {
            setWidthOverrides((prev) => {
                const next = mutator(prev);
                try {
                    if (Object.keys(next).length === 0) {
                        localStorage.removeItem(storageKey);
                    } else {
                        localStorage.setItem(storageKey, JSON.stringify(next));
                    }
                } catch {
                    // ignore (private mode, quota)
                }
                return next;
            });
        },
        [storageKey],
    );

    const setWidth = useCallback(
        (colId, px) => {
            const numeric = Math.round(Math.max(MIN_W, Math.min(MAX_W, Number(px) || 0)));
            persistFn((prev) => ({ ...prev, [colId]: numeric }));
        },
        [persistFn],
    );

    /**
     * Batch update — aceita objeto { colId: px }. Usado pra autofit-all
     * (dblclick no header da coluna 'select') pra setar todas as larguras
     * num único re-render sem perda por stale closure.
     */
    const setWidths = useCallback(
        (updates) => {
            const sanitized = {};
            for (const [colId, px] of Object.entries(updates || {})) {
                sanitized[colId] = Math.round(Math.max(MIN_W, Math.min(MAX_W, Number(px) || 0)));
            }
            persistFn((prev) => ({ ...prev, ...sanitized }));
        },
        [persistFn],
    );

    const resetColumn = useCallback(
        (colId) => {
            persistFn((prev) => {
                if (!(colId in prev)) return prev;
                const next = { ...prev };
                delete next[colId];
                return next;
            });
        },
        [persistFn],
    );

    const resetAll = useCallback(() => persistFn(() => ({})), [persistFn]);

    return { widthOverrides, setWidth, setWidths, resetColumn, resetAll };
}

function load(storageKey) {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

/**
 * Resolve o `w` final da coluna pro grid-template:
 *   1. Override do localStorage (se existir) — usuário arrastou ou autofittou.
 *   2. `colDefs[col].w` — definição padrão da coluna.
 *   3. '80px' — fallback final.
 *
 * Quando `isSorted=true`, mede o label e cresce SE NÃO COUBER o ícone de sort
 * junto. Se a coluna já tem espaço sobrando (label estreito vs w generoso),
 * mantém o tamanho — pra evitar "tomar resize sem necessidade".
 *
 * Retorna sempre uma string CSS válida (px ou fr).
 */
export function ResolveColumnWidth(col, colDefs, overrides, isSorted = false) {
    const override = overrides?.[col];
    const baseW = Number.isFinite(override)
        ? `${override}px`
        : colDefs[col]?.w || COL_DEFS[col]?.w || '80px';

    if (!isSorted) return baseW;

    // Colunas com unidade fr (ex: Produto = '1.8fr') já são elásticas — não
    // somamos px num fr. A coluna naturalmente cabe o ícone.
    if (/fr$/.test(String(baseW).trim())) return baseW;

    // Mede o label e calcula o espaço mínimo necessário pra caber label + ícone.
    const def = colDefs[col] || COL_DEFS[col];
    const label = String(def?.label || col).toUpperCase();
    const labelW = measureText(label, HEADER_FONT) + label.length * 0.5;
    const required = Math.ceil(labelW + HEADER_PADDING_LR_PX + SORT_ICON_SLOT_PX);

    const basePx = Number.parseInt(String(baseW), 10);
    if (Number.isFinite(basePx) && basePx >= required) {
        return baseW; // já tem espaço de sobra → não cresce
    }
    return `${required}px`;
}

/**
 * Medição de texto via canvas — rápido, sem reflow forçado. Usado pelo autofit
 * e pelo cresce-quando-sortado em resolveColumnWidth.
 *
 * `font` deve refletir o estilo real do elemento (`{weight} {size}px {family}`).
 * Aceita strings com letras maiúsculas convertidas previamente (uppercase no CSS
 * é renderizado depois — medir o texto cru com a fonte certa funciona pra ~98%
 * dos casos; pra acertar 100% precisaria gerar uppercase manual antes de medir).
 */
// Canvas reutilizado entre medições — evita criar elemento novo a cada autofit.
let _measureCanvas = null;
function measureText(text, font) {
    if (!_measureCanvas) {
        _measureCanvas = document.createElement('canvas');
    }
    const ctx = _measureCanvas.getContext('2d');
    ctx.font = font;
    return ctx.measureText(String(text ?? '')).width;
}

/**
 * Calcula a largura ideal pra renderizar SÓ o label do header (sem considerar
 * conteúdo das linhas). Usado quando o usuário dá duplo clique na zona do
 * separador que cobre o header.
 */
export function ComputeAutofitForHeader(col, colDefs, headerFont) {
    const def = colDefs[col] || COL_DEFS[col];
    const label = def?.label || col;
    // Label é renderizado UPPERCASE com letterSpacing 0.5 — somamos 0.5 * chars.
    const upper = String(label).toUpperCase();
    const textWidth = measureText(upper, headerFont) + upper.length * 0.5;
    return Math.ceil(textWidth + HEADER_OVERHEAD_PX);
}

/**
 * Calcula a largura ideal pra renderizar a CÉLULA mais larga da coluna nas
 * `rows` fornecidas. Usado quando o usuário dá duplo clique na zona do
 * separador que cobre os dados (comportamento Excel).
 */
// Cap pra autofit automático (dblclick) — sistema NUNCA aumenta uma coluna
// além disso. User ainda pode arrastar manualmente pra largura maior. Aplicado
// igual no autoWidths default (DataTable) e no onAutofit (dblclick header).
export const AUTO_WIDTH_MAX_PX = 160;

export function ComputeAutofitForContent(col, rows, cellFont, columnView = null, ctx = null) {
    let maxWidth = 0;
    for (const row of rows) {
        const raw = getCellValue(row, col, ctx);
        if (raw === null || raw === undefined) continue;
        // `bestDay` renderiza "qty DD/MM" (qty mono + data menor à direita).
        // getCellValue devolve só a qty pra sort/export estáveis — pro autofit
        // precisamos contar o texto inteiro pra largura real, senão a coluna
        // colapsa e a data corta com "...".
        let txt = String(raw);
        if (col === 'bestDay') {
            txt = `${raw} 99/99`; // "99/99" = pior caso da data DD/MM (5 chars)
        }
        const w = measureText(txt, cellFont);
        if (w > maxWidth) {
            maxWidth = w;
        }
    }
    // Quando a coluna está em modo gráfico (line/bar), o autofit precisa
    // considerar a largura do chart + número à direita, senão o duplo clique
    // colapsa a coluna pro tamanho só do número e o sparkline fica espremido.
    // Line: Spark 100px + gap 6 + número (~20-40px) → ~140px mínimo.
    // Bar: usa min-width ~80px pra a barra ter pelo menos algum preenchimento.
    if (columnView === 'line') {
        // 100 (spark) + 6 (gap) + measured text + 10 padding
        return Math.ceil(Math.max(maxWidth, 0) + 100 + 6 + CELL_OVERHEAD_PX);
    }
    if (columnView === 'bar') {
        return Math.ceil(Math.max(maxWidth + CELL_OVERHEAD_PX, 90));
    }
    return Math.ceil(maxWidth + CELL_OVERHEAD_PX);
}
